import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const NotificationContext = createContext(null);

// Polling interval in milliseconds
const POLL_INTERVAL = 15000; // 15 seconds

export const NotificationProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [newMatches, setNewMatches] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [lastChecked, setLastChecked] = useState(null);
  const previousUnread = useRef(0);
  const pollIntervalRef = useRef(null);

  // Fetch notification counts
  const fetchNotificationCounts = useCallback(async () => {
    if (!token) return;
    
    try {
      // Get unread message count
      const unreadRes = await api.getUnreadCount();
      const newUnreadCount = unreadRes.data.unread_count || 0;
      
      // Show toast if new messages arrived (and not first load)
      if (previousUnread.current !== null && newUnreadCount > previousUnread.current) {
        const diff = newUnreadCount - previousUnread.current;
        toast.info(`${diff} new message${diff > 1 ? 's' : ''}!`, {
          description: "Check your messages",
          action: {
            label: "View",
            onClick: () => window.location.href = "/messages"
          }
        });
      }
      
      previousUnread.current = newUnreadCount;
      setUnreadMessages(newUnreadCount);
      
      // Get match stats for new matches count
      const statsRes = await api.getMatchStats();
      const stats = statsRes.data;
      
      // Count pending matches as "new"
      const pendingMatches = await api.getDailyMatches();
      const pending = pendingMatches.data.matches?.filter(m => m.status === "pending").length || 0;
      setNewMatches(pending);
      
      setLastChecked(new Date());
    } catch (error) {
      console.error("Failed to fetch notification counts:", error);
    }
  }, [token]);

  // Start polling when user is logged in
  useEffect(() => {
    if (user && token) {
      // Use setTimeout to avoid synchronous setState in effect
      const initialFetch = setTimeout(() => {
        fetchNotificationCounts();
      }, 100);
      
      // Set up polling
      pollIntervalRef.current = setInterval(fetchNotificationCounts, POLL_INTERVAL);
      
      return () => {
        clearTimeout(initialFetch);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    } else {
      // Clear counts when logged out - use timeout to avoid sync setState
      const clearCounts = setTimeout(() => {
        setUnreadMessages(0);
        setNewMatches(0);
        previousUnread.current = 0;
      }, 0);
      return () => clearTimeout(clearCounts);
    }
  }, [user, token, fetchNotificationCounts]);

  // Add a notification
  const addNotification = useCallback((notification) => {
    const id = Date.now();
    setNotifications(prev => [{
      id,
      ...notification,
      timestamp: new Date(),
      read: false
    }, ...prev].slice(0, 50)); // Keep last 50 notifications
    
    return id;
  }, []);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  // Clear a notification
  const clearNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // Refresh counts manually
  const refreshCounts = useCallback(() => {
    fetchNotificationCounts();
  }, [fetchNotificationCounts]);

  // Calculate total unread
  const totalUnread = unreadMessages + newMatches;
  const unreadNotifications = notifications.filter(n => !n.read).length;

  const value = {
    unreadMessages,
    newMatches,
    notifications,
    totalUnread,
    unreadNotifications,
    lastChecked,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearNotification,
    refreshCounts
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};
